import { useEffect, useState } from "react";
import { api, formatError, notifyOperatorsChanged } from "../lib/api";
import { PageHeader, Modal } from "../components/ui-kit";
import { Plus, Trash2, Pencil, Building2 } from "lucide-react";

const EMPTY = { name: "", code: "", color: "#0EA5E9", active: true };

export default function OperatorsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");

  const load = () => api.get("/operators").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setErr(""); setOpen(true); };
  const openEdit = (op) => {
    setEditing(op);
    setForm({ name: op.name, code: op.code || "", color: op.color || "#0EA5E9", active: !!op.active });
    setErr(""); setOpen(true);
  };

  const save = async () => {
    setErr("");
    if (!form.name.trim()) { setErr("Nome é obrigatório"); return; }
    try {
      if (editing) await api.put(`/operators/${editing.id}`, form);
      else await api.post("/operators", form);
      setOpen(false); load(); notifyOperatorsChanged();
    } catch (e) { setErr(formatError(e)); }
  };

  const del = async (op) => {
    if (!window.confirm(`Excluir a operadora ${op.name}?`)) return;
    try { await api.delete(`/operators/${op.id}`); load(); notifyOperatorsChanged(); }
    catch (e) { alert(formatError(e)); }
  };

  return (
    <div data-testid="operators-page">
      <PageHeader
        eyebrow="Multi-marca"
        title="Operadoras"
        actions={<button className="btn btn-primary" onClick={openNew} data-testid="new-operator-btn"><Plus size={14} /> Nova Operadora</button>}
      >
        Cadastre as operadoras que sua equipe atende (MEO, NOS, Vodafone, etc.). Materiais, equipamentos, tipos de serviço e OTs ficam separados por operadora. Cada usuário só vê as operadoras que você liberar em <b>Usuários</b>.
      </PageHeader>

      <div className="card">
        <table className="data">
          <thead>
            <tr><th style={{ width: 40 }}></th><th>Nome</th><th>Código</th><th>Status</th><th>Ações</th></tr>
          </thead>
          <tbody>
            {items.map((op) => (
              <tr key={op.id} data-testid={`operator-row-${op.id}`}>
                <td><span style={{ display: "inline-block", width: 18, height: 18, borderRadius: 999, background: op.color || "#0EA5E9" }} /></td>
                <td className="font-semibold">{op.name}</td>
                <td className="mono">{op.code || "-"}</td>
                <td>{op.active ? <span className="badge badge-done">Ativa</span> : <span className="badge badge-cancel">Inativa</span>}</td>
                <td>
                  <div className="flex gap-1">
                    <button className="btn btn-ghost" onClick={() => openEdit(op)} data-testid={`edit-operator-${op.id}`}><Pencil size={14} /> Editar</button>
                    <button className="btn btn-ghost" onClick={() => del(op)} data-testid={`del-operator-${op.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8">
                <div className="flex flex-col items-center gap-2" style={{ color: "var(--text-mute)" }}>
                  <Building2 size={24} />
                  <div className="text-sm">Nenhuma operadora cadastrada ainda.</div>
                  <div className="text-xs">Comece criando MEO, NOS, Vodafone ou outra marca que sua equipe atende.</div>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar Operadora" : "Nova Operadora"} testid="operator-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="save-operator-btn">{editing ? "Salvar" : "Criar"}</button>
        </>}>
        {err && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{err}</div>}
        <div className="grid gap-3">
          <div>
            <label className="label-caps block mb-1">Nome</label>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: MEO, NOS, Vodafone" data-testid="operator-name" />
          </div>
          <div>
            <label className="label-caps block mb-1">Código (opcional)</label>
            <input className="input mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ex: MEO" data-testid="operator-code" />
          </div>
          <div>
            <label className="label-caps block mb-1">Cor (identidade visual)</label>
            <div className="flex items-center gap-3">
              <input type="color" value={form.color || "#0EA5E9"} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="operator-color" style={{ width: 44, height: 32, border: "1px solid var(--border-strong)", background: "transparent" }} />
              <span className="mono text-xs" style={{ color: "var(--text-mute)" }}>{form.color}</span>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="operator-active-toggle">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <span className="text-sm">Ativa</span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
